/**
 * Tag Typeahead Component for Alpine.js
 * Provides autocomplete functionality for tag selection
 */

export function tagTypeahead(initialTags = [], pageType = 'page') {
  return {
    // State
    tags: initialTags || [],
    query: '',
    suggestions: [],
    isOpen: false,
    selectedIndex: -1,
    isLoading: false,
    debounceTimer: null,

    // Lifecycle
    init() {
      // Click outside to close
      this.$el.addEventListener('click', (e) => e.stopPropagation());
      document.addEventListener('click', () => {
        this.isOpen = false;
      });
    },

    // Methods
    addTag(tagName) {
      const trimmedTag = tagName.trim();
      if (trimmedTag && !this.tags.includes(trimmedTag)) {
        this.tags.push(trimmedTag);
        this.query = '';
        this.suggestions = [];
        this.isOpen = false;
        this.selectedIndex = -1;
        this.updateHiddenInput();
      }
    },

    removeTag(index) {
      this.tags.splice(index, 1);
      this.updateHiddenInput();
    },

    selectSuggestion(tagName) {
      this.addTag(tagName);
    },

    async fetchSuggestions() {
      if (this.query.length < 1) {
        this.suggestions = [];
        this.isOpen = false;
        return;
      }

      this.isLoading = true;

      try {
        const url = `/admin/pages/tags/search?page_type=${pageType}&q=${encodeURIComponent(this.query)}`;
        const response = await fetch(url, {
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (!response.ok) throw new Error('Failed to fetch suggestions');

        const data = await response.json();

        // Filter out already selected tags
        this.suggestions = data
          .filter(tag => !this.tags.includes(tag.name))
          .map(tag => tag.name);

        // Add "Create new tag" option if query doesn't match any existing tag
        const exactMatch = this.suggestions.some(s => s.toLowerCase() === this.query.toLowerCase());
        if (!exactMatch && this.query.trim()) {
          this.suggestions.unshift(`+ Create "${this.query}"`);
        }

        this.isOpen = this.suggestions.length > 0;
        this.selectedIndex = -1;
      } catch (error) {
        console.error('Error fetching tag suggestions:', error);
        this.suggestions = [];
        this.isOpen = false;
      } finally {
        this.isLoading = false;
      }
    },

    onInput() {
      // Debounce the search
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.fetchSuggestions();
      }, 300);
    },

    onKeydown(event) {
      if (!this.isOpen && event.key !== 'Enter') return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.selectedIndex = Math.min(this.selectedIndex + 1, this.suggestions.length - 1);
          break;

        case 'ArrowUp':
          event.preventDefault();
          this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
          break;

        case 'Enter':
          event.preventDefault();
          if (this.selectedIndex >= 0 && this.selectedIndex < this.suggestions.length) {
            const suggestion = this.suggestions[this.selectedIndex];
            const tagName = suggestion.startsWith('+ Create "')
              ? this.query
              : suggestion;
            this.selectSuggestion(tagName);
          } else if (this.query.trim()) {
            // If no suggestion selected, add the query as a new tag
            this.addTag(this.query);
          }
          break;

        case 'Escape':
          event.preventDefault();
          this.isOpen = false;
          this.selectedIndex = -1;
          break;

        case ',':
          event.preventDefault();
          if (this.query.trim()) {
            this.addTag(this.query);
          }
          break;
      }
    },

    updateHiddenInput() {
      const hiddenInput = this.$el.querySelector('input[type="hidden"]');
      if (hiddenInput) {
        hiddenInput.value = this.tags.join(', ');
      }
    },

    getSuggestionClass(index) {
      const baseClasses = 'px-3 py-2 cursor-pointer';
      const hoverClasses = 'hover:bg-gray-100';
      const selectedClasses = this.selectedIndex === index ? 'bg-blue-50 text-blue-700' : '';
      const createClasses = this.suggestions[index]?.startsWith('+ Create "') ? 'font-medium text-blue-600' : '';

      return `${baseClasses} ${hoverClasses} ${selectedClasses} ${createClasses}`.trim();
    },

    getSuggestionValue(suggestion) {
      // Extract the tag name from "Create" suggestions
      return suggestion.startsWith('+ Create "') ? this.query : suggestion;
    }
  };
}

// Make available globally for Alpine
window.tagTypeahead = tagTypeahead;
